import { Badge, Dropdown, Flex, Space, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import ButtonComponent from "../../../../Components/Button"
import Private from "../../../../Components/Private"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import {
  alertsColumnIconMap,
  dateFormat,
  serviceMap,
  statusTextMap,
  tagColorMap,
  tagMap
} from "../../../../Utils/utils"

dayjs.extend(utc)

const { Text } = Typography

export const getColumns = ({ theme, options, handleChangeStatus, isLoyaltyBillingForAdmin }) => ({
  status: {
    key: "status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Status",
        sorter: true,
        icon: alertsColumnIconMap["status"],
        stateData: state,
        key: "status"
      }),
    dataIndex: "status",
    fixed: "left",
    width: 128,
    render: (_, record) => {
      if (isLoyaltyBillingForAdmin && _?.toLowerCase() === "pending") {
        return (
          <Dropdown
            disabled={_?.toLowerCase() !== "pending"}
            menu={{
              items: options?.map((el) => {
                let statusLowerCase = el?.value?.toLowerCase()
                return {
                  key: el?.value,
                  label: (
                    <Flex gap={8} align="center">
                      <Badge
                        status={tagMap[statusLowerCase] || "default"}
                        color={tagColorMap[el?.value]}
                      />
                      <Text>{statusTextMap[el?.value] || el?.label}</Text>
                    </Flex>
                  )
                }
              }),
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation()
                if (key !== _) handleChangeStatus({ id: record.id, newStatus: key })
              }
            }}
          >
            <Tag
              color={tagMap[_?.toLowerCase()] || "default"}
              key={_}
              style={{ margin: 0 }}
              className={"simple large"}
            >
              <Flex gap={8} align="center">
                <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
                <Text>{statusTextMap[_] || _}</Text>

                <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
              </Flex>
            </Tag>
          </Dropdown>
        )
      } else {
        return (
          <Tag
            color={tagMap[_?.toLowerCase()] || "default"}
            key={_}
            style={{ margin: 0 }}
            className={"simple large"}
          >
            <Flex gap={8} align="center">
              <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
              <Text>{statusTextMap[_] || _}</Text>
            </Flex>
          </Tag>
        )
      }
    }
  },
  topup_type: {
    key: "topup_type",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Type",
        sorter: true,
        icon: "focus",
        stateData: state,
        key: "topup_type"
      }),
    dataIndex: "topup_type",
    width: 150
  },
  invoice_amount: {
    key: "invoice_amount",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Amount",
        sorter: true,
        icon: alertsColumnIconMap["invoice_amount"],
        stateData: state,
        key: "invoice_amount"
      }),
    dataIndex: "invoice_amount",
    width: 128,
    render: (_, record) => (
      <Text>
        <Private>{`${record?.currency || "$"} ${_}`}</Private>
      </Text>
    )
  },
  date: {
    key: "date",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Date",
        sorter: true,
        icon: alertsColumnIconMap["date"],
        stateData: state,
        key: "date"
      }),
    dataIndex: "date",
    width: 156,
    render: (_) =>
      _ ? (
        <Space direction="vertical" size={4}>
          <Text>{dayjs(_).format(dateFormat)}</Text>
          <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
        </Space>
      ) : (
        "—"
      )
  },
  card_last4: {
    key: "card_last4",
    title: (state) =>
      titleSortFilters({
        title: "Payment Method",
        icon: alertsColumnIconMap["card_last4"],
        stateData: state,
        key: "card_last4"
      }),
    dataIndex: "card_last4",
    render: (_, record) => (
      <Space>
        {record?.brand && (
          <img
            src={Images[`${record?.brand?.toLowerCase()}${theme === "dark" ? "_dark" : ""}`]}
            width={20}
            alt={record?.brand}
          />
        )}
        {_ && (
          <>
            <Text>••••</Text>
            <Text>{_}</Text>
          </>
        )}
      </Space>
    ),
    width: 168
  },
  is_reissued: {
    key: "is_reissued",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Reissued",
        sorter: true,
        icon: alertsColumnIconMap["is_reissued"],
        stateData: state,
        key: "is_reissued"
      }),
    dataIndex: "is_reissued",
    width: 140,
    render: (_) => (_ ? "Yes" : "No")
  },
  paid_with: {
    key: "paid_with",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Paid with",
        sorter: true,
        icon: alertsColumnIconMap["paid_with"],
        stateData: state,
        key: "paid_with"
      }),
    dataIndex: "paid_with",
    width: 132
  },
  balance_before: {
    key: "balance_before",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Balance Before",
        sorter: true,
        icon: alertsColumnIconMap["amount"],
        stateData: state,
        key: "balance_before"
      }),
    dataIndex: "balance_before",
    width: 148,
    render: (value) =>
      value != null ? (
        <Text>
          <Private>{`$ ${value}`}</Private>
        </Text>
      ) : (
        "—"
      )
  },
  balance_after: {
    key: "balance_after",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Balance After",
        sorter: true,
        icon: alertsColumnIconMap["amount"],
        stateData: state,
        key: "balance_after"
      }),
    dataIndex: "balance_after",
    width: 148,
    render: (value) =>
      value != null ? (
        <Text>
          <Private>{`$ ${value}`}</Private>
        </Text>
      ) : (
        "—"
      )
  },
  invoice_id: {
    key: "invoice_id",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Transaction ID",
        sorter: true,
        icon: alertsColumnIconMap["invoice_id"],
        stateData: state,
        key: "invoice_id"
      }),
    dataIndex: "invoice_id",
    width: 185,
    ellipsis: true,
    render: (_) => (_ ? <Private>{_}</Private> : "—")
  }
})

export const getDefaultColumn = ({ field_id, label }) => ({
  key: field_id,
  sorter: true,
  title: (state) =>
    titleSortFilters({
      title: label,
      sorter: true,
      icon: alertsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  width: 185,
  ellipsis: true,
  render: (_) => _ || "—"
})

export const getActionColumn = ({ onSelectRow, visible, externalDemoMode }) => {
  if (!visible?.["download_invoice"] && !visible?.["view_details"]) return null

  const handleAction = (e, data, type) => {
    e.preventDefault()
    e.stopPropagation()
    onSelectRow(data, type)
  }
  return {
    key: "actions",
    title: "",
    width: 70,
    render: (_, record) => (
      <Flex>
        {visible?.["download_invoice"] &&
          ["Paid", "Failed", "Declined"].includes(record?.status) && (
            <Tooltip title="Download invoice">
              <ButtonComponent
                type="link"
                icon={<Icon type={"arrow_inbox"} />}
                onClick={(e) => handleAction(e, record, "download_invoice")}
                className="flex center "
                disabled={externalDemoMode}
              />
            </Tooltip>
          )}

        {visible?.["view_details"] && (
          <Tooltip title="View details">
            <ButtonComponent
              type="link"
              icon={<Icon type={"layout_align_right"} />}
              onClick={(e) => handleAction(e, record, "view_details")}
              className="flex center "
            />
          </Tooltip>
        )}
      </Flex>
    )
  }
}

export const getAlertsColumns = ({ t }) => [
  {
    key: "alert_id",
    title: t("Alert ID"),
    dataIndex: "alert_id"
  },
  {
    key: "alert_service",
    title: t("Alert source"),
    dataIndex: "alert_service",
    render: (_) => serviceMap?.[_] || _
  },
  {
    key: "alert_timestamp",
    title: t("Alert date"),
    dataIndex: "alert_timestamp",
    render: (_) => (
      <Space direction="vertical" size={4}>
        <Text>{dayjs(_).format(dateFormat)}</Text>
        <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
      </Space>
    )
  }
]
